import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appsCoreApiRouterListRichContent, RichContentListOut } from '@/lib/api';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, User, Tag, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LogBook = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<RichContentListOut[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLogBookEntries = async () => {
      try {
        setLoading(true);
        const response = await appsCoreApiRouterListRichContent({ content_type: 'LOG_BOOK' });
        setEntries(response.data || []);
      } catch (error) {
        console.error('Error fetching log book entries:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogBookEntries();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {Array(3).fill(0).map((_, i) => (
          <Card key={i} className="glass">
            <CardHeader>
              <div className="space-y-2">
                <Skeleton className="h-6 w-3/4" />
                <div className="flex gap-4">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Skeleton className="h-4 w-full mb-2" />
              <Skeleton className="h-4 w-2/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="glass rounded-xl p-8">
          <h3 className="heading-md text-white mb-4">No Log Book Entries</h3>
          <p className="text-adv-lightGray">
            No published log book entries available at the moment. Check back soon for new adventure stories!
          </p>
        </div>
      </div>
    );
  }

  return (
    <section className="mb-16 md:mb-24">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8">
        <div>
          <h2 className="heading-lg text-white mb-2">The Logbook</h2>
          <p className="text-adv-lightGray max-w-2xl">
            Read about previous rallies, tips and tricks, and thoughts from the Exodus crew.
          </p>
        </div>
      </div>
      
      <div className="space-y-6">
        {entries.map((entry) => (
          <Card key={entry.id} className="glass hover:bg-adv-lightGray/50 transition-colors">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1">
                  <h3 className="heading-md mb-2 hover:text-adv-accent transition-colors">
                    {entry.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-adv-lightGray">
                    <div className="flex items-center gap-1">
                      <User size={14} />
                      <span>{entry.author_name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      <span>{formatDate(entry.created_at)}</span>
                    </div>
                    {entry.tag_list.length > 0 && (
                      <div className="flex items-center gap-1">
                        <Tag size={14} />
                        <div className="flex gap-1">
                          {entry.tag_list.slice(0, 3).map((tag, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {entry.tag_list.length > 3 && (
                            <Badge variant="secondary" className="text-xs">
                              +{entry.tag_list.length - 3}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {entry.is_featured && (
                  <Badge variant="default">
                    Featured
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {entry.image_url && (
                  <div className="aspect-video rounded-lg overflow-hidden">
                    <img
                      src={entry.image_url}
                      alt={entry.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div>
                  {entry.summary && (
                    <p className="mb-3">{entry.summary}</p>
                  )}
                  {entry.content_preview && (
                    <p className="text-adv-lightGray text-sm leading-relaxed">
                      {entry.content_preview}
                    </p>
                  )}
                </div>
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/log-book/${entry.id}`)}
                    className="text-adv-accent border-adv-accent hover:bg-adv-accent/10"
                  >
                    Read More
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};

export default LogBook; 